import type { BrowserOptions, ErrorEvent } from "@sentry/react";
import { scrubPii } from "./errorReporting.helpers";

// Client error reporting (#1081). The real `@sentry/react` SDK is *injected*
// (see `initErrorReporting` + main.tsx) rather than imported here, so this
// module — and anything that imports it, e.g. ErrorBoundary.tsx — stays free of
// the heavy SDK and the `import.meta` ts-jest trap, and the factory unit-tests
// against a stub. Mirrors the createAnalytics factory in analytics.ts.

// Minimal Sentry surface we depend on. The real `* as Sentry from "@sentry/react"`
// is structurally assignable to this (params are wider).
export interface SentryLike {
  init: (options: BrowserOptions) => unknown;
  captureException: (
    error: unknown,
    captureContext?: {
      tags?: Record<string, string>;
      contexts?: Record<string, Record<string, unknown>>;
    },
  ) => string;
  setTags: (tags: Record<string, string>) => void;
}

export interface ErrorReportingConfig {
  // Public DSN, build-time-injected. Empty string ⇒ disabled (dev / E2E / any
  // build without VITE_SENTRY_DSN), the same "unset key ⇒ off" idiom as GA4.
  dsn: string;
  // Build commit — Sentry `release`, ties a crash to the exact bundle.
  release: string;
  environment?: string;
}

// Context attached to a captured crash. All optional — whatever is known at the
// crash site. Surfaced as filterable Sentry tags (+ the React component stack).
export interface ErrorContext {
  orgId?: string;
  version?: string;
  route?: string;
  componentStack?: string;
  // Names a failure that has no exception of its own (#1491) — a stalled cold
  // start, a lazy-route chunk that failed to load — so each is one filterable
  // Sentry bucket rather than a message-string search.
  failure?: string;
  // Which cold-start gate was still pending: "code" | "namespace" | "org-config".
  loadStage?: string;
  // navigator.onLine at capture time — separates a dead connection from a
  // live-but-stalled one.
  online?: boolean;
  // The oldest network read still pending at a stall, or "none" (#1129): names
  // WHICH await hung, where `loadStage` only names the gate.
  stalledOn?: string;
  // Every read pending at a stall, oldest first ("<label> (<ms>ms)"). Sent as
  // a context rather than a tag: tags are for filtering, this is for reading.
  pendingReads?: string[];
}

// Drop undefined/empty so we never ship literal "undefined" tag values.
const toTags = (context: ErrorContext): Record<string, string> => {
  const tags: Record<string, string> = {};
  if (context.orgId) tags.orgId = context.orgId;
  if (context.version) tags.configVersion = context.version;
  if (context.route) tags.route = context.route;
  if (context.failure) tags.failure = context.failure;
  if (context.loadStage) tags.loadStage = context.loadStage;
  if (context.online !== undefined) tags.online = String(context.online);
  // Sentry caps a tag value at 200 chars.
  if (context.stalledOn) tags.stalledOn = context.stalledOn.slice(0, 200);
  return tags;
};

const toContexts = (
  context: ErrorContext,
): Record<string, Record<string, unknown>> | undefined => {
  const contexts: Record<string, Record<string, unknown>> = {};
  if (context.componentStack) {
    contexts.react = { componentStack: context.componentStack };
  }
  if (context.pendingReads) {
    contexts.pendingReads = { reads: context.pendingReads };
  }
  return Object.keys(contexts).length ? contexts : undefined;
};

export const createErrorReporting = (
  sentry: SentryLike,
  config: ErrorReportingConfig,
) => {
  const enabled = !!config.dsn;
  let initialized = false;

  const init = (): void => {
    if (initialized || !enabled) return;
    sentry.init({
      dsn: config.dsn,
      release: config.release || undefined,
      environment: config.environment,
      // Errors only — no performance/replay sampling.
      tracesSampleRate: 0,
      // Strip PII (emails/handles, cookies, headers) before transport.
      beforeSend: (event: ErrorEvent) => scrubPii(event),
    });
    initialized = true;
  };

  const captureError = (error: unknown, context: ErrorContext = {}): void => {
    if (!initialized) return;
    sentry.captureException(error, {
      tags: toTags(context),
      contexts: toContexts(context),
    });
  };

  const setContextTags = (context: ErrorContext): void => {
    if (!initialized) return;
    sentry.setTags(toTags(context));
  };

  return { init, captureError, setContextTags };
};

// ── Module singleton ────────────────────────────────────────────────────────
// main.tsx calls initErrorReporting(Sentry, …) once at startup (Sentry imported
// there, not here). captureError/setContextTags are safe no-ops until then, so
// ErrorBoundary.tsx can import them unconditionally.

let instance: ReturnType<typeof createErrorReporting> | null = null;

export const initErrorReporting = (
  sentry: SentryLike,
  config: ErrorReportingConfig,
): void => {
  instance = createErrorReporting(sentry, config);
  instance.init();
};

export const captureError = (error: unknown, context?: ErrorContext): void =>
  instance?.captureError(error, context);

export const setContextTags = (context: ErrorContext): void =>
  instance?.setContextTags(context);
